import { ParsedItem } from 'store/declaration-form-errors/ParsedForm';
import countParsedItemErrors from './countParsedItemErrors';
import { ItemErrorCounts } from './getRejectedItemErrorCounts';

/** A product's errors by where they come from: the customs rejection and the form validation. */
export type ProductErrorCount = { customs: number; validation: number };
export type ProductsErrorCounts = { [rowIndex: number]: ProductErrorCount };

/**
 * The errors of each row on the page being shown. The errors belong to items, but the tables mark
 * rows - and a row is the item's position on the page, which sorting, paging and deleting all
 * change. So the rows are worked out from the ids, every time the page changes.
 *
 * Validation items of declaration types whose items cannot be found by id keep marking the row they
 * were counted at.
 */
const getErrorCountsByRow = (
    products: { id?: string }[],
    validationItems: ParsedItem[],
    rejectedErrorCountByItemId: ItemErrorCounts
): ProductsErrorCounts => {
    const errorCountsByRow: ProductsErrorCounts = {};
    const validationErrorCountByItemId = new Map(
        validationItems.filter(({ id }) => id).map((item) => [item.id, countParsedItemErrors(item)])
    );

    products.forEach((product, rowIndex) => {
        if (!product.id) return;

        const customs = rejectedErrorCountByItemId[product.id] ?? 0;
        const validation = validationErrorCountByItemId.get(product.id) ?? 0;
        if (customs || validation) errorCountsByRow[rowIndex] = { customs, validation };
    });

    validationItems
        .filter(({ id }) => !id)
        .forEach((item) => {
            const rowIndex = item.index - 1;
            const rowCount = errorCountsByRow[rowIndex] ?? { customs: 0, validation: 0 };
            errorCountsByRow[rowIndex] = { ...rowCount, validation: rowCount.validation + countParsedItemErrors(item) };
        });

    return errorCountsByRow;
};

export default getErrorCountsByRow;
